import { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay/legacy';
import { connect } from 'react-redux';

import * as tracking from 'dibs-tracking';
import serverVars from 'server-vars';
import { CUSTOMER_TYPE_TRADE } from '../constants/userConstants';
import { pageTypeConstants as PAGE_TYPES } from '../constants/pageTypeConstants';
import { getPageviewData } from '../utils/tracking/searchBrowse/getPageviewData';

import { BuySharedHead } from './BuySharedHead/BuySharedHead';
import { SharedUserAndRelayVarLoader } from '../shared/SharedUserAndRelayVarLoader';
import { BuySharedBreadcrumbs } from './BuySharedBreadcrumbs/BuySharedBreadcrumbs';
import { BuySharedTitle } from './BuySharedTitle/BuySharedTitle';
import { BuySharedRelatedPages } from './BuySharedRelatedPages/BuySharedRelatedPages';
import { BuySharedResultContainer } from './BuySharedResultContainer/BuySharedResultContainer';
import { SharedPagination } from '../shared/SharedPagination/SharedPagination';
import { SharedPopularSearchesLink } from '../shared/SharedPopularSearchesLink/SharedPopularSearchesLink';
import { BuySharedDescription } from './BuySharedDescription/BuySharedDescription';
import { BuySharedNullSearchResults } from './BuySharedNullSearchResults/BuySharedNullSearchResults';
import { BuySharedCMSFAQ } from './BuySharedCMSFAQ/BuySharedCMSFAQ';
import { BuySharedFAQ } from './BuySharedFAQ/BuySharedFAQ';
import { BuySharedMoreWaysToBrowse } from './BuySharedMoreWaysToBrowse/BuySharedMoreWaysToBrowse';
import { SharedBottomModule } from '../shared/SharedBottomModule/SharedBottomModule';
import { LoadingComponent } from '../components/global/LoadingComponent';
import { SharedWordpressArticles } from '../shared/SharedWordpressArticles/SharedWordpressArticles';

import { BuyRespTopNav } from './BuyRespTopNav/BuyRespTopNav';
import { BuyMobileNav } from './BuyMobileNav/BuyMobileNav';
import { ServerVarsContextConsumer } from '../global/ServerVarsContext/ServerVarsContext';
import { BuySharedContentModuleRegular } from './BuySharedContentModuleRegular/BuySharedContentModuleRegular';

import { updateRelayVariables } from '../actions/sharedRelayVariableActions';
import { toggleRequestInFlight, updateUriRef } from '../actions/filterActions';

import { getNewPaginationUriRef, addCdnQueryParams } from '../utils/uriUtils';

import { FindingRouter } from '../router/FindingRouter';

import styles from './BuySharedLayout.scss';

class BuySharedLayoutComponent extends Component {
    constructor() {
        super();

        this.handleUserRefetch = this.handleUserRefetch.bind(this);
        this.handleBuyPageRefetch = this.handleBuyPageRefetch.bind(this);
        this.refetchCallback = this.refetchCallback.bind(this);
        this.handlePageChange = this.handlePageChange.bind(this);
    }

    componentDidMount() {
        const { viewer } = this.props;
        const { buyPage } = viewer;
        const { pageType, totalResults, appliedFilters, buyPageSbUriRef: displayUriRef } = buyPage;
        this.buyPageName = appliedFilters.find(
            f => f.name === PAGE_TYPES.BUY
        )?.values?.[0]?.urlLabel;
        this.router = new FindingRouter({ routes: [{ path: '/buy/:pageName?' }] });
        this.props.updateUriRef({ uriRef: displayUriRef });
        tracking.trackPageview({
            additional: {
                ...getPageviewData({
                    pageType,
                    totalResults,
                    appliedFilters,
                }),
            },
        });
    }

    componentDidUpdate(prevProps) {
        const { generatedUriRef, page: newPage } = this.props;
        const oldUriRef = addCdnQueryParams(prevProps.generatedUriRef);
        const newUriRef = addCdnQueryParams(generatedUriRef);
        const uriRefChanged = oldUriRef !== '' && newUriRef !== oldUriRef;
        const pageChanged = newPage !== prevProps.page;
        let uriRef = generatedUriRef;

        if (pageChanged) {
            uriRef = getNewPaginationUriRef(generatedUriRef, newPage);
            this.handlePageChange(newPage);
        }

        if (uriRefChanged) {
            const variables = { uriRef, page: newPage };
            this.props.toggleRequestInFlight(true);
            this.handleBuyPageRefetch(
                { ...variables, fetchBuy: true, skipUser: true },
                null,
                this.refetchCallback
            );
            this.props.updateRelayVariables(variables);
        }
    }

    refetchCallback() {
        this.props.toggleRequestInFlight(false);
        this.props.updateUriRef({ uriRef: this.props?.viewer?.buyPage?.buyPageSbUriRef });
    }

    handlePageChange(page) {
        if (this.buyPageName) {
            this.router.navigate(
                getNewPaginationUriRef(`/${PAGE_TYPES.BUY}/${this.buyPageName}/`, page),
                { replace: true }
            );
        }
    }

    handleUserRefetch(variables, renderVariables, callback) {
        /**
         * Need to refetch sb items for trade users, the price depends on the `isTrade` variable
         * being set.
         */
        const fetchBuy = variables.isTrade && serverVars.get('userType') !== CUSTOMER_TYPE_TRADE;
        this.handleBuyPageRefetch(
            {
                ...variables,
                fetchBuy,
                skipUser: true,
            },
            renderVariables,
            callback
        );
    }

    handleBuyPageRefetch(variables, renderVariables, callback) {
        this.props.relay.refetch(variables, renderVariables, callback);
    }

    render() {
        const { viewer, relay, isClient, page, refetchInFlight } = this.props;
        const {
            buyPage,
            user = null,
            isWordpressArticlesEnabled,
            isPopularSearchesEnabled,
        } = viewer;
        const {
            internalBuyLinks,
            browseContentModule,
            bottomModule,
            filters,
            buyPageSbUriRef: displayUriRef,
            displayMaxNumberOfPages,
            popularSearchesLink,
        } = buyPage;

        const showNullSearchResultPage = !buyPage.totalResults;

        return (
            <ServerVarsContextConsumer>
                {({ isMobile }) => (
                    <Fragment>
                        <BuySharedHead buyPage={buyPage} />
                        <SharedUserAndRelayVarLoader onUserRefetch={this.handleUserRefetch} />
                        {isMobile && refetchInFlight && <LoadingComponent />}
                        {showNullSearchResultPage ? (
                            <BuySharedNullSearchResults viewer={viewer} buyPage={buyPage} />
                        ) : (
                            <Fragment>
                                <BuySharedBreadcrumbs buyPage={buyPage} />
                                <BuySharedTitle buyPage={buyPage} />
                                <BuySharedRelatedPages buyPage={buyPage} />
                                {isPopularSearchesEnabled && (
                                    <div className={styles.topSectionPopularSearches}>
                                        <SharedPopularSearchesLink
                                            popularSearchesLink={popularSearchesLink}
                                            popularSearchTermClassName={
                                                styles.popularSearchCategoryTerm
                                            }
                                            isTopPlacement
                                        />
                                    </div>
                                )}
                                <BuySharedContentModuleRegular
                                    browseContentModule={browseContentModule}
                                />

                                {isMobile ? (
                                    <BuyMobileNav
                                        viewer={viewer}
                                        buyPage={buyPage}
                                        user={user}
                                        filters={filters}
                                        isClient={isClient}
                                    />
                                ) : (
                                    <BuyRespTopNav viewer={viewer} buyPage={buyPage} user={user} />
                                )}

                                <div className={styles.container}>
                                    <BuySharedResultContainer
                                        viewer={viewer}
                                        buyPage={buyPage}
                                        relay={relay}
                                    />
                                    <SharedPagination
                                        currentPage={page}
                                        numberOfPages={displayMaxNumberOfPages}
                                        displayUriRef={displayUriRef}
                                        hideHref
                                    />
                                    <SharedBottomModule
                                        className={styles.bottomModule}
                                        bottomModule={bottomModule}
                                    />
                                    <BuySharedDescription buyPage={buyPage} />
                                </div>

                                <BuySharedCMSFAQ buyPage={buyPage} />
                                <BuySharedFAQ buyPage={buyPage} />

                                {isWordpressArticlesEnabled && (
                                    <SharedWordpressArticles itemSearch={buyPage} />
                                )}

                                <BuySharedMoreWaysToBrowse
                                    viewer={viewer}
                                    itemSearch={buyPage}
                                    internalBuyLinks={internalBuyLinks}
                                    popularSearchTermClassName={styles.popularSearchCategoryTerm}
                                />
                            </Fragment>
                        )}
                    </Fragment>
                )}
            </ServerVarsContextConsumer>
        );
    }
}

function mapStateToProps(state) {
    const { filters, relayVariables } = state;
    const { generatedUriRef, page } = filters;
    const {
        variables: { isClient },
    } = relayVariables;

    return {
        page,
        generatedUriRef,
        refetchInFlight: filters.refetchInFlight,
        isClient,
    };
}

BuySharedLayoutComponent.propTypes = {
    viewer: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    generatedUriRef: PropTypes.string,
    isClient: PropTypes.bool.isRequired,
    toggleRequestInFlight: PropTypes.func.isRequired,
    updateUriRef: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
    refetchInFlight: PropTypes.bool.isRequired,
    updateRelayVariables: PropTypes.func.isRequired,
    buyPage: PropTypes.object,
};

export const BuySharedLayout = createRefetchContainer(
    connect(mapStateToProps, { toggleRequestInFlight, updateUriRef, updateRelayVariables })(
        BuySharedLayoutComponent
    ),
    {
        viewer: graphql`
            fragment BuySharedLayout_viewer on Viewer {
                isWordpressArticlesEnabled: featureFlag(feature: "wordpressArticles")
                isPopularSearchesEnabled: featureFlag(feature: "popularSearches")
                buyPage: itemSearch(
                    uriRef: $uriRef
                    localeOrigin: $localeOrigin
                    first: $first
                    page: $page
                    buyPageId: $rootId
                ) {
                    buyPageSbUriRef
                    pageType
                    totalResults
                    displayMaxNumberOfPages
                    appliedFilters {
                        name
                        values {
                            displayName
                            urlLabel
                            code
                        }
                    }
                    filters {
                        ...BuyMobileNav_filters
                    }
                    browseContentModule {
                        ...BuySharedContentModuleRegular_browseContentModule
                    }
                    bottomModule {
                        ...SharedBottomModule_bottomModule
                    }
                    popularSearchesLink {
                        ...SharedPopularSearchesLink_popularSearchesLink
                    }
                    ...BuySharedBreadcrumbs_buyPage
                    ...BuySharedTitle_buyPage
                    ...BuySharedRelatedPages_buyPage
                    ...BuySharedResultContainer_buyPage
                    ...BuySharedDescription_buyPage
                    ...BuySharedHead_buyPage
                    ...BuySharedNullSearchResults_buyPage
                    ...BuySharedCMSFAQ_buyPage
                    ...BuySharedFAQ_buyPage
                    ...SharedWordpressArticles_itemSearch
                    ...BuySharedMoreWaysToBrowse_itemSearch
                    internalBuyLinks {
                        ...BuySharedMoreWaysToBrowse_internalBuyLinks
                    }
                    ...BuyRespTopNav_buyPage
                    ...BuyMobileNav_buyPage
                }
                ...BuySharedMoreWaysToBrowse_viewer
                ...BuySharedResultContainer_viewer
                ...BuySharedNullSearchResults_viewer
                ...BuyRespTopNav_viewer
                ...BuyMobileNav_viewer
                user(userId: $userId) @include(if: $fetchUser) {
                    ...BuyRespTopNav_user
                    ...BuyMobileNav_user
                }
            }
        `,
    },
    graphql`
        query BuySharedLayoutRefetchQuery(
            $uriRef: String!
            $localeOrigin: String!
            $isTrade: Boolean!
            $showSeller: Boolean!
            $first: Int!
            $page: Int!
            $rootId: String!
            $fetchBuy: Boolean!
            $userZipCode: String = ""
            $userCountryCode: String = ""
            $fetchRegionalInfo: Boolean = false
            $userId: String = ""
            $fetchUser: Boolean!
            $skipUser: Boolean = true
        ) {
            viewer {
                isWordpressArticlesEnabled: featureFlag(feature: "wordpressArticles")
                buyPage: itemSearch(
                    uriRef: $uriRef
                    localeOrigin: $localeOrigin
                    first: $first
                    page: $page
                    buyPageId: $rootId
                ) @include(if: $fetchBuy) {
                    pageType
                    totalResults
                    displayMaxNumberOfPages
                    buyPageSbUriRef
                    appliedFilters {
                        name
                        values {
                            displayName
                            urlLabel
                            code
                        }
                    }
                    browseContentModule {
                        ...BuySharedContentModuleRegular_browseContentModule
                    }
                    bottomModule {
                        ...SharedBottomModule_bottomModule
                    }
                    ...BuySharedBreadcrumbs_buyPage
                    ...BuySharedTitle_buyPage
                    ...BuySharedRelatedPages_buyPage
                    ...BuySharedResultContainer_buyPage
                    ...BuySharedDescription_buyPage
                    ...BuySharedHead_buyPage
                    ...BuySharedNullSearchResults_buyPage
                    ...BuySharedCMSFAQ_buyPage
                    ...BuySharedFAQ_buyPage
                    ...SharedWordpressArticles_itemSearch
                    ...BuySharedMoreWaysToBrowse_itemSearch
                    internalBuyLinks {
                        ...BuySharedMoreWaysToBrowse_internalBuyLinks
                    }
                    ...BuyRespTopNav_buyPage
                }

                user(userId: $userId) @include(if: $fetchUser) @skip(if: $skipUser) {
                    ...BuyRespTopNav_user
                    ...BuyMobileNav_user
                }
                ...BuySharedResultContainer_viewer
            }
        }
    `
);
